import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import './index.css'
import App from './App.jsx'
import ErrorBoundary from './ui/ErrorBoundary.jsx'
import { initClientLogReporter, reportEventNow } from './engine/clientLogReporter.js'
import { setMemReporter } from './engine/memBudget.js'
import { reportLastSession } from './engine/crashBlackbox.js'
import { init as initPwa } from '@engine/pwaInstall';
import { checkNow as checkUpdateLog } from '@engine/updateLog';
// 지오메트리 누수 추적기 — 기본 꺼져 있고 `?geoleak=1` 또는 콘솔 `__geoLeak.on()` 으로만 켜진다.
// ⚠ 「import 만으로는 아무 비용도 없다」는 **틀린 말이었다**. 그 파일은
//   `import * as THREE from 'three'` 를 하므로, 정적 import 하는 순간 **로그인 화면이
//   Three.js 2MB 를 받는다.** 런타임 비용이 0인 것과 전송 비용이 0인 것은 다른 문제다.
//   그래서 켤 때만 받는다. 콘솔 진입점(`__geoLeak.on()`)은 아래 스텁이 그대로 유지한다.
if (typeof window !== 'undefined') {
  const wantGeoLeak = (() => {
    try { return new URLSearchParams(window.location.search).get('geoleak') === '1'; }
    catch { return false; }
  })();
  const load = () => import('./engine/geoLeakProbe.js');
  if (wantGeoLeak) load();
  else if (!window.__geoLeak) {
    // 아직 안 받았을 때의 얇은 스텁 — 누가 콘솔에서 켜면 그때 진짜를 받아 넘긴다.
    window.__geoLeak = {
      async on() { await load(); return window.__geoLeak.on(); },
      async report() { await load(); return window.__geoLeak.report(); },
    };
  }
}

// 모바일 디버깅: ?debug=1 → 인페이지 콘솔(eruda) + 에러 발생 시 자동 서버 리포트(client_debug JSONL)
initClientLogReporter();
// 메모리 예산 경고를 서버 리포터로 잇는다 — memBudget 이 로거를 **직접 import 하면**
// 지형 워커(hfWorker)가 그 사슬을 통째로 삼켜 564KB 가 된다(memBudget.js 주석 참조).
// 그래서 메인 스레드에서만 꽂는다.
setMemReporter(reportEventNow);
// 지난 세션이 인사 없이 끝났으면 로컬 이력에 남긴다 — 서버로는 안 보낸다.
// 로그인 화면 「크래시 로그」 버튼(CrashLogPanel.jsx)이 유저 본인 기기에서 직접 보여준다.
// 자동 서버 전송은 로컬 개발 + chrome-devtools-mcp 로 직접 볼 수 있게 되며 없앴다(명관님 지시).
reportLastSession();

// ─── 배포 중 남아있던 탭의 청크 로드 실패 자동 복구 ──────────────
// safedeploy로 새 빌드가 나가면 JS 청크 해시가 전부 바뀌는데, 그 전에 이미 열려있던 탭이 lazy()로
// 지워진 옛 청크를 불러오려 하면 실패한다(2026-07-21, 소셜 패널 lazy import 중 실제 발생 —
// "Failed to fetch dynamically imported module" → ErrorBoundary 크래시 화면까지 감). Vite가 정확히
// 이 상황에 vite:preloadError 이벤트를 쏴주므로, 새로고침 한 번으로 최신 청크를 받게 자동 복구.
// 15초 내 재시도 이력이 있으면(그 새로고침 후에도 또 실패) 무한루프 방지로 포기하고 ErrorBoundary가
// 잡게 둔다 — 그땐 진짜 배포/네트워크 문제라 새로고침 버튼을 눌러도 사용자가 알아채는 게 낫다.
//
// ★ 2026-08-22 — **이 복구는 안 듣고 있었다.** 실기 로그에 같은 실패가 9초 간격으로 두 번
//   찍혔다(back/logs/client_debug/2026-08-22, 세션 akmed2k5 -> fsz2k1n8, 둘 다
//   `RpgWorld.jsx` 청크 실패 -> `<CanvasImpl>` 크래시 -> webglcontextlost). 즉 새로고침은
//   **했는데 똑같이 죽었다.** 이유는 `location.reload()` 가 **문서 캐시를 우회하지 않는 것**이다 —
//   낡은 index.html 이 다시 와서 이미 사라진 청크 해시를 또 물고, 그 다음엔 15초 가드에 막혀
//   재시도조차 못 한다. 그 상태가 곧 명관님이 보시는 **「HUD 만 뜨고 3D 는 영영 안 오는」** 화면이다
//   (HUD 는 이미 그려진 DOM 이라 살아 있고, 3D 를 담은 청크만 못 받는다).
//
//   그래서 셋을 바꾼다:
//     ① 새로고침 **전에** `fetch(cache:'reload')` 로 문서를 강제로 새로 받아 HTTP 캐시를 갱신한다.
//     ② 청크 캐시(서비스워커)에 남은 옛 .js 도 지운다 — cacheFirst 라 안 지우면 또 옛 걸 준다.
//     ③ 가드를 「15초 내 무시」에서 **「2회까지 시도, 그 뒤 포기」**로 바꾼다. 한 번으로 안 되는
//        경우가 실제로 있었는데 옛 가드는 그 두 번째를 막고 있었다.
//
//   포기할 때는 조용히 두지 않는다 — ErrorBoundary 가 새로고침 버튼을 띄우게 둔다.
const recoverFromDeadChunk = async (why) => {
  // ⚠ 이미 월드가 서 있으면 건드리지 않는다 — 세션 도중 곁가지 청크 하나가 실패했다고
  //   새로고침하면 **멀쩡히 놀고 있던 판을 깨는 것**이다.
  //
  //   ★ 판정을 `window.__r3f` 로 하면 안 된다.
  //     캔버스는 **메인 청크**에 있어 언제나 뜨고 `onCreated` 도 돈다 — 3D 월드만 지연
  //     청크(`RpgWorld`)에 있다. 그래서 고착 상태의 실측값이 **`r3f=true · calls=17 · mesh=0`**
  //     이었다. 즉 `__r3f` 존재는 「3D 가 산다」가 아니라 **고장 상태에서도 참**이다.
  //     살아 있는지는 「월드가 실제로 그려지나」로 묻는다.
  const mesh = window.__perfStats?.meshes ?? 0;
  if (mesh > 50) { console.warn('[chunk] 실패했지만 월드는 이미 서 있다 — 새로고침 안 함:', why); return; }
  const KEY = 'vite_reload_guard';
  let st = {};
  try { st = JSON.parse(sessionStorage.getItem(KEY) || '{}'); } catch { /* 손상 시 초기화 */ }
  const n = Number(st.n || 0);
  if (n >= 2) { console.error('[chunk] 두 번 시도했는데 또 실패 — 포기한다', why); return; }
  try { sessionStorage.setItem(KEY, JSON.stringify({ n: n + 1, t: Date.now() })); } catch { /* noop */ }
  console.warn(`[chunk] 죽은 청크 감지(${why}) — 문서·청크 캐시를 비우고 ${n + 1}번째 재시도`);
  // ② 옛 JS 청크를 캐시에서 지운다(SW 는 .js 를 cacheFirst 로 준다).
  try {
    if (window.caches) {
      for (const k of await caches.keys()) {
        const c = await caches.open(k);
        for (const req of await c.keys()) if (/\.js(\?|$)/.test(new URL(req.url).pathname)) await c.delete(req);
      }
    }
  } catch { /* 캐시 접근 실패는 무시 — 아래 강제 재요청이 본 수단이다 */ }
  // ① 문서를 캐시 우회로 다시 받아 둔다. 이래야 이어지는 reload 가 새 index.html 을 쓴다.
  try { await fetch(window.location.href, { cache: 'reload', credentials: 'same-origin' }); } catch { /* noop */ }
  window.location.reload();
};

window.addEventListener('vite:preloadError', () => { recoverFromDeadChunk('vite:preloadError'); });

// vite 가 이벤트를 못 쏘는 경로도 있다 — 실기 로그의 그 실패는 **ErrorBoundary 까지 갔다**
// (`react-crash ... at CanvasImpl`). 즉 preloadError 만으로는 다 못 잡는다. 메시지로 한 번 더 건진다.
//
// ⚠ 좁게 잡는다 — 새로고침은 **멀쩡한 세션을 죽일 수 있는** 조치다. 둘을 같이 요구한다:
//   ① 문구가 「동적 import 실패」일 것  ② 그 주소가 **우리 앱 청크**(`/assets/…js`)일 것.
//   ②가 없으면 언마운트 중 취소된 곁가지 import(예: 크래시 블랙박스·로그 리포터)까지
//   새로고침 사유가 된다. 그건 고치는 게 아니라 멀쩡한 판을 깨는 것이다.
const looksLikeDeadChunk = (m) => typeof m === 'string'
  && /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed/i.test(m)
  && /\/assets\/[\w.-]+\.js/.test(m);
window.addEventListener('unhandledrejection', (e) => {
  const m = e?.reason?.message || String(e?.reason || '');
  if (looksLikeDeadChunk(m)) recoverFromDeadChunk('unhandledrejection');
});
window.addEventListener('error', (e) => {
  if (looksLikeDeadChunk(e?.message)) recoverFromDeadChunk('window.error');
});

// ─── Service Worker 등록 + 매니페스트 기반 캐시 갱신 ──────────────
async function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) return;

  // DEV: SW 절대 등록 안 함. 기존 SW + 캐시 모두 자동 청소 → 매번 수동으로 비울 필요 없음.
  if (import.meta.env.DEV) {
    try {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
      if (window.caches) {
        const keys = await caches.keys();
        // ⚠ 지형 LOD 메시 캐시는 **살려 둔다.** 그건 SW 캐시가 아니라 이 앱이 직접 굽는
        //   산출물이고(`lodMeshCache`), 여기서 같이 지우면 dev 에서는 적중이 영영 0 이라
        //   「캐시가 느리다」로 오독한다(2026-09-20 에 실제로 그랬다).
        await Promise.all(keys.filter((k) => !k.startsWith('terrain-lod-mesh'))
          .map((k) => caches.delete(k)));
      }
      if (regs.length || (window.caches && (await caches.keys()).length)) {
        console.info('[SW] dev mode — 기존 SW/캐시 정리 완료. 새로고침 1회 권장.');
      }
    } catch (e) {
      console.warn('[SW] dev 정리 실패:', e);
    }
    return;
  }

  try {
    const reg = await navigator.serviceWorker.register('/sw.js', { scope: '/' });

    // 이미 등록된 SW 가 있으면 브라우저는 갱신을 미룰 수 있다(내비게이션 스로틀링).
    // sw.js 는 nginx 가 no-cache 로 주므로 매 접속 강제 확인 = 캐시 버그 수정이 즉시 퍼진다.
    reg.update?.().catch(() => { /* 오프라인 등은 무시 */ });

    // SW가 활성화된 후 manifest 비교
    const sw = reg.active || reg.installing || reg.waiting;
    if (!sw) return;

    const onActive = async () => {
      try {
        const res = await fetch('/asset_manifest.json', { cache: 'no-store' });
        if (!res.ok) return;
        const manifest = await res.json();
        sw.postMessage({ type: 'CHECK_MANIFEST', manifest });
      } catch { /* manifest 없으면 스킵 */ }
    };

    if (reg.active) {
      onActive();
    } else {
      sw.addEventListener('statechange', (e) => {
        if (e.target.state === 'activated') onActive();
      });
    }
  } catch (err) {
    console.error('[SW] 등록 실패:', err);
  }
}

registerServiceWorker();

// StrictMode 는 개발 때 이펙트를 두 번 돌려 3D 초기화가 겹친다. 누수 검사가 필요할 때만 주소에 ?strict=1.
const appTree = (
  <ErrorBoundary>
    <App />
  </ErrorBoundary>
);
const useStrict = new URLSearchParams(window.location.search).has('strict');

createRoot(document.getElementById('root')).render(
  useStrict ? <StrictMode>{appTree}</StrictMode> : appTree,
)

// 설치 안내 창구를 열고 저장을 영구로 올려 달라 청한다(`pwaInstall` 머리말).
initPwa();

// 업데이트 내역 — 앱이 뜬 뒤 «한가할 때» 한 번 견준다. 달라졌을 때만 한 줄 쌓인다.
//   여기서 안 부르면 「시스템 정보」를 연 사람만 이력이 생겨, 정작 바뀐 판을 놓친다.
if (typeof requestIdleCallback === 'function') requestIdleCallback(() => checkUpdateLog(), { timeout: 8000 });
else setTimeout(() => checkUpdateLog(), 3000);
